import { getCurrentScope, onScopeDispose, watch } from 'vue'

/**
 * Composable for handling shop registration errors
 * Detects "account already exists" errors and displays appropriate messages with links
 * Owns and deduplicates its banners, clearing them on resubmit, locale change, or scope disposal
 */
export function useShopRegistrationError() {
  const { t, locale } = useI18n()
  const localePath = useLocalePath()
  const { pushError, removeOne, notifications } = useNotifications()
  const owned = new Map<number, string>()
  // Set when the owning scope (page) is disposed: a register request still in
  // flight at that moment must not create a banner nobody can clear.
  let disposed = false

  const clearRegistrationErrors = (): void => {
    for (const id of owned.keys()) removeOne(id)
    owned.clear()
  }

  const pushRegistrationError = (message: string): void => {
    if ([...owned.values()].includes(message)) return

    const before = new Set(notifications.value.map((n) => n.id))
    pushError(message, { persistent: true })
    for (const n of notifications.value) {
      if (!before.has(n.id)) owned.set(n.id, n.message)
    }
  }

  interface ShopwareError {
    code?: string
    detail?: string
    status?: string
    title?: string
  }

  type ErrorContainer = {
    details?: {
      errors?: ShopwareError[]
    }
    errors?: ShopwareError[]
    response?: {
      _data?: {
        errors?: ShopwareError[]
      }
    }
    messages?: Array<{ code?: string; detail?: string }>
    message?: string
    statusMessage?: string
  }

  const isErrorContainer = (error: unknown): error is ErrorContainer => typeof error === 'object' && error !== null

  const DUPLICATE_ERROR_KEY = 'shop.registrationPage.accountAlreadyExistsError'
  const LOGIN_LINK_KEY = 'shop.registrationPage.accountAlreadyExistsLoginLink'
  const FORGOT_PASSWORD_LINK_KEY = 'shop.registrationPage.accountAlreadyExistsForgotPasswordLink'
  const GENERIC_ERROR_KEY = 'shop.registrationPage.registrationErrorMessage'

  /**
   * Diagnostic synthesized by @shopware/api-client ApiClientError when the API
   * returned no error body (e.g. HTTP 503 with empty body). Internal English
   * debugging text, never user-facing in any locale.
   */
  const SDK_SYNTHETIC_DETAIL = 'API did not return errors, but request failed. Please check the network tab.'

  const LAST_RESORT_MESSAGE = 'Account creation failed'

  const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0

  /**
   * Resolve a translation, returning null when unavailable. Installed vue-i18n
   * returns the requested key for missing translations, which must not leak
   * into user-facing notifications.
   */
  const translateOrNull = (key: string, params?: Record<string, string>): string | null => {
    const translated: unknown = params ? t(key, params) : t(key)
    if (!isNonEmptyString(translated)) return null
    if (translated === key) return null
    return translated
  }

  /**
   * Escape untrusted backend text before it reaches the v-html notification renderer.
   */
  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  /**
   * Fail-closed generic message; never blank and never a raw translation key.
   */
  const fallbackMessage = (): string => translateOrNull(GENERIC_ERROR_KEY) ?? LAST_RESORT_MESSAGE

  /**
   * A backend detail is usable only when present and not SDK-synthesized
   * missing-detail text. Legitimate server validation messages pass through.
   */
  const hasUsableDetail = (detail: unknown): detail is string =>
    isNonEmptyString(detail) && detail.trim() !== SDK_SYNTHETIC_DETAIL

  /**
   * Check if the error indicates an account already exists.
   * The Shopware code is the primary signal; the detail fallback only matches
   * narrow duplicate phrasing so unrelated "already ..." messages do not qualify.
   */
  const isAccountExistsError = (error: ShopwareError): boolean => {
    const errorCode = error.code || ''

    if (
      errorCode === 'VIOLATION::CUSTOMER_EMAIL_NOT_UNIQUE' ||
      /CUSTOMER.*EMAIL.*(NOT_UNIQUE|ALREADY_EXISTS|EXISTS)/i.test(errorCode)
    ) {
      return true
    }

    const errorDetail = (error.detail || '').toLowerCase()
    return /already (used|exists|registered|taken)|already in use|not unique|duplicate/i.test(errorDetail)
  }

  /**
   * Build the "account already exists" error message with links.
   * Recovery links are trusted markup; backend text is never interpolated here.
   * Falls back to the generic message when the template or either link label
   * is unavailable, so users never see empty or key-labelled links.
   */
  const buildAccountExistsMessage = (): string => {
    const loginLabel = translateOrNull(LOGIN_LINK_KEY)
    const forgotPasswordLabel = translateOrNull(FORGOT_PASSWORD_LINK_KEY)
    if (loginLabel === null || forgotPasswordLabel === null) return fallbackMessage()

    const loginUrl = localePath('/shop/login')
    const recoveryUrl = localePath('/shop/account/recovery')
    const message = translateOrNull(DUPLICATE_ERROR_KEY, {
      loginLink: `<a href="${loginUrl}">${loginLabel}</a>`,
      forgotPasswordLink: `<a href="${recoveryUrl}">${forgotPasswordLabel}</a>`,
    })

    return message ?? fallbackMessage()
  }

  /**
   * Handle a single error and push appropriate notification
   */
  const handleError = (error: ShopwareError): void => {
    if (isAccountExistsError(error)) {
      pushRegistrationError(buildAccountExistsMessage())
      return
    }

    pushRegistrationError(hasUsableDetail(error.detail) ? escapeHtml(error.detail) : fallbackMessage())
  }

  /**
   * Extract errors from various possible locations in the error object
   */
  const extractErrorsFromException = (error: unknown): ShopwareError[] => {
    const e = isErrorContainer(error) ? error : {}

    // ApiClientError details (Shopware API client)
    if (e?.details?.errors && Array.isArray(e.details.errors)) {
      return e.details.errors
    }

    // Direct errors array
    if (e?.errors && Array.isArray(e.errors)) {
      return e.errors
    }

    // Response data (Nuxt/ofetch wrapped)
    if (e?.response?._data?.errors && Array.isArray(e.response._data.errors)) {
      return e.response._data.errors
    }

    // ClientApiError messages format (legacy)
    if (e?.messages && Array.isArray(e.messages)) {
      return e.messages.map((m: { code?: string; detail?: string }) => ({
        code: m.code,
        detail: m.detail,
      }))
    }

    return []
  }

  /**
   * Handle registration errors from a caught exception
   * @param error - The caught error object
   */
  const handleCaughtError = (error: unknown): void => {
    if (disposed) return
    clearRegistrationErrors()
    const errors = extractErrorsFromException(error)

    if (errors.length === 0) {
      // No usable structured validation detail: technical exception text
      // (ofetch network diagnostics, SDK "Failed request" compositions, HTTP
      // status text) is never user-facing. Use the localized generic fallback.

      pushRegistrationError(fallbackMessage())
      return
    }

    errors.forEach((err) => {
      handleError(err)
    })
  }

  watch(locale, () => clearRegistrationErrors())
  if (getCurrentScope()) {
    onScopeDispose(() => {
      disposed = true
      clearRegistrationErrors()
    })
  }

  return {
    handleCaughtError,
    clearRegistrationErrors,
  }
}
